import notification from "antd/es/notification";
import { defineMessages } from "react-intl";
import { all, call, put, select, take, takeEvery } from "redux-saga/effects";
import { isActionOf } from "typesafe-actions";

import DuplicateStatus from "@mapmycustomers/shared/enum/DuplicateStatus";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import {
  EntityType,
  EntityTypeSupportingDataMerging,
  Person,
} from "@mapmycustomers/shared/types/entity";
import Company from "@mapmycustomers/shared/types/entity/Company";
import { DuplicatePair, DuplicateRun } from "@mapmycustomers/shared/types/entity/Duplicate";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import { initialize } from "@app/component/createEditEntity/CreateDealModal/store/actions";
import getDescriptionWithActionsNode from "@app/component/createEditEntity/util/getDescriptionWithActionsNode";
import configService from "@app/config/ConfigService";
import i18nService from "@app/config/I18nService";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { MERGE_DUPLICATES_TABLE_PAGE_SIZE } from "@app/scene/settings/util/const";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";

import EntityPriority from "../enum/EntityPriority";
import { DUPLICATE_ACTIVE_STATUSES } from "../util/const";

import {
  changeDuplicatePairDismission,
  checkDupeRunStatus,
  fetchDuplicates,
  hideDismissed,
  mergeDuplicates,
  positionSelectedPair,
  recalculateDuplicates,
  selectDuplicatePair,
  selectEntityType,
  setLoading,
  setSearchText,
  showDismissed,
} from "./actions";
import {
  getDuplicates,
  getEntityType,
  getPage,
  getSearchText,
  getSelectedAsPrioritizedEntity,
  getSelectedPair,
  getSelectedPairIndex,
  isDismissedShowed,
} from "./selectors";

const messages = defineMessages({
  successNotificationDescription: {
    id: "settings.mergeDuplicates.actions.successNotification.description",
    defaultMessage: "We’ve successfully completed our check for duplicate records.",
    description: "Description of the Dupes Check Success Notification on the Merge Duplicates page",
  },
  successNotificationTitle: {
    id: "settings.mergeDuplicates.actions.successNotification.title",
    defaultMessage: "Duplicate check completed",
    description: "Title of the Dupes Check Success Notification on the Merge Duplicates page",
  },
});

export function* onFetchDuplicates() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const entityType: EntityTypeSupportingDataMerging = yield select(getEntityType);
    const showDismissed: boolean = yield select(isDismissedShowed);
    const page: number = yield select(getPage);

    let query: string = yield select(getSearchText);
    query = query.trim();
    yield put(setLoading(true));
    const $filters: PlatformFilterModel = {
      status: showDismissed ? DuplicateStatus.DISMISSED : { $in: DUPLICATE_ACTIVE_STATUSES },
    };
    if (query.length) {
      $filters.query = query;
    }

    const [response, runResponse]: [ListResponse<DuplicatePair>, DuplicateRun] = yield all([
      callApi("getEntityDuplicates", orgId, entityType, {
        $filters,
        $limit: MERGE_DUPLICATES_TABLE_PAGE_SIZE,
        $offset: page * MERGE_DUPLICATES_TABLE_PAGE_SIZE,
      }),
      callApi("getDuplicateRun", orgId, entityType),
    ]);
    const lastCalculatedDate = runResponse.updatedAt;
    yield put(
      fetchDuplicates.success({
        duplicates: response.data,
        lastCalculatedDate,
        total: response.total,
      })
    );
  } catch (error) {
    yield put(fetchDuplicates.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onCheckDupeRunStatus({ payload }: ReturnType<typeof checkDupeRunStatus.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: DuplicateRun = yield callApi("getDuplicateRun", orgId, payload ?? undefined);
    yield put(
      checkDupeRunStatus.success({
        lastCalculatedDate: response.updatedAt,
        lastDupeRunStatus: response.status,
      })
    );
  } catch (error) {
    yield put(checkDupeRunStatus.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onChangeDuplicatePairDismission({
  payload: { dismissed, pair },
}: ReturnType<typeof changeDuplicatePairDismission.request>) {
  try {
    yield put(positionSelectedPair());
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const entityType: EntityTypeSupportingDataMerging = yield select(getEntityType);
    yield callApi("updateDuplicatePair", orgId, entityType, {
      ...pair,
      status: dismissed ? DuplicateStatus.DISMISSED : DuplicateStatus.OPEN,
    });
    notification.success({
      message: dismissed
        ? i18nService.getIntl()?.formatMessage({
            id: "settings.mergeDuplicates.dismiss.notification",
            defaultMessage: "Suggested duplicate dismissed",
            description: "Settings Merge duplicates - Suggested duplicate dismissed",
          })
        : i18nService.getIntl()?.formatMessage({
            id: "settings.mergeDuplicates.undoDismission.notification",
            defaultMessage: "Dismission undone",
            description: "Settings Merge duplicates - Dismission undone",
          }),
    });
    yield put(changeDuplicatePairDismission.success());
  } catch (error) {
    yield put(changeDuplicatePairDismission.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onMergeDuplicates() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const entityType: EntityTypeSupportingDataMerging = yield select(getEntityType);
    const pair: DuplicatePair = yield select(getSelectedPair);
    const priority: EntityPriority = yield select(getSelectedAsPrioritizedEntity);
    yield callApi(
      "mergeDuplicates",
      orgId,
      entityType,
      pair.id,
      priority === EntityPriority.PRIMARY ? pair.primary.id : pair.secondary.id,
      priority === EntityPriority.PRIMARY ? pair.secondary.id : pair.primary.id
    );

    yield put(fetchDuplicates.request({}));
    const initializeResult: ReturnType<
      typeof fetchDuplicates.failure | typeof fetchDuplicates.success
    > = yield take([fetchDuplicates.success, fetchDuplicates.failure]);
    if (isActionOf(fetchDuplicates.failure, initializeResult)) {
      yield put(initialize.failure());
      return;
    }
    const intl = i18nService.getIntl();
    notification.info({
      description: intl?.formatMessage({
        id: "settings.mergeDuplicates.merging.info.description",
        defaultMessage:
          "The duplicate records are being merged. This action may take up to a few minutes. Once merged successfully, they will disappear from the duplicate table.",
        description: "Settings Merge duplicates - merge duplicates info description",
      }),
      message: intl?.formatMessage({
        id: "settings.mergeDuplicates.merging.info.message",
        defaultMessage: "Merging in progress",
        description: "Settings Merge duplicates - merge duplicates info description",
      }),
    });
    yield put(positionSelectedPair());
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onSelectDuplicatePair({
  payload,
}: ReturnType<typeof selectDuplicatePair.request>) {
  if (payload !== undefined) {
    try {
      const orgId: Organization["id"] = yield select(getOrganizationId);
      const entityType: EntityTypeSupportingDataMerging = yield select(getEntityType);
      const duplicates: DuplicatePair[] = yield select(getDuplicates);
      const duplicate = duplicates[payload];
      const response: ListResponse<Company | Person> = yield callApi(
        entityType === EntityType.COMPANY ? "fetchCompanies" : "fetchPeople",
        orgId,
        {
          $filters: {
            $and: [
              {
                id: {
                  [PlatformFilterOperator.CONTAINS]: [duplicate.primary.id, duplicate.secondary.id],
                },
              },
            ],
            includeCustomFields: true,
          },
        }
      );
      const primary: Company | Person | undefined = response.data.find(
        ({ id }) => id === duplicate.primary.id
      );
      const secondary: Company | Person | undefined = response.data.find(
        ({ id }) => id === duplicate.secondary.id
      );
      if (primary && secondary) {
        yield put(selectDuplicatePair.success({ primary, secondary }));
      }
    } catch (error) {
      yield put(selectDuplicatePair.failure(error));
      yield put(handleError({ error }));
    }
  }
}

export function* onPositionSelectedPair() {
  const index: number | undefined = yield select(getSelectedPairIndex);
  if (index === undefined) {
    return;
  }
  const duplicates: DuplicatePair[] = yield select(getDuplicates);
  const indexes = duplicates.map((_, i) => i);
  const newIndex = [...indexes.slice(index), ...indexes.slice(0, index - 1).reverse()].find(
    (i) => duplicates[i].status === DuplicateStatus.OPEN
  );
  yield put(selectDuplicatePair.request(newIndex === -1 ? undefined : newIndex));
}

function* onRecalculateDuplicates({ payload }: ReturnType<typeof recalculateDuplicates.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: DuplicateRun = yield callApi(
      "recalculateDuplicates",
      orgId,
      payload ?? undefined
    );
    yield put(
      recalculateDuplicates.success({
        entityType: payload,
        lastCalculatedDate: response.updatedAt,
        lastDupeRunStatus: response.status,
      })
    );

    const mergeDuplicatesPagePath = `${Path.SETTINGS}/${SettingPath.FORMS_MERGE_DUPLICATES}`;
    const isOnMergeDuplicatePage = false; // window.location.pathname === mergeDuplicatesPagePath;

    if (isOnMergeDuplicatePage) {
      yield put(fetchDuplicates.request({ page: 0 }));
    }

    const notificationKey = "duplicatesSearchDone";
    yield call(notification.success, {
      description: getDescriptionWithActionsNode({
        description: i18nService.formatMessage(
          messages.successNotificationDescription,
          "We’ve successfully completed our check for duplicate records."
        ),
        intl: i18nService.getIntl(),
        onActionClick: isOnMergeDuplicatePage
          ? undefined
          : () => {
              window.open(`//${configService.getBaseAppUrl()}${mergeDuplicatesPagePath}`);
              notification.close(notificationKey);
            },
        onCloseClick: isOnMergeDuplicatePage
          ? undefined
          : () => {
              notification.close(notificationKey);
            },
      }),
      duration: isOnMergeDuplicatePage ? undefined : 0,
      key: notificationKey,
      message: i18nService.formatMessage(
        messages.successNotificationTitle,
        "Duplicate check completed"
      ),
    });
  } catch (error) {
    yield put(recalculateDuplicates.failure(payload));
    yield put(handleError({ error }));
  }
}

export default onRecalculateDuplicates;

export function* mergeDuplicatesSaga() {
  yield takeEvery(
    [
      changeDuplicatePairDismission.success,
      fetchDuplicates.request,
      hideDismissed,
      selectEntityType,
      setSearchText,
      showDismissed,
    ],
    onFetchDuplicates
  );
  yield takeEvery(changeDuplicatePairDismission.request, onChangeDuplicatePairDismission);
  yield takeEvery(selectDuplicatePair.request, onSelectDuplicatePair);
  yield takeEvery(mergeDuplicates, onMergeDuplicates);
  yield takeEvery(positionSelectedPair, onPositionSelectedPair);
  yield takeEvery(recalculateDuplicates.request, onRecalculateDuplicates);
  yield takeEvery(checkDupeRunStatus.request, onCheckDupeRunStatus);
}
